/**
 * Global AdSense cleanup utility
 * 
 * This function removes all traces of AdSense from the page when a user
 * transitions to a paid tier. This ensures:
 * 1. No AdSense network requests for paid users
 * 2. No ad slots rendered in the DOM
 * 3. Complete cleanup of global state
 */

declare global {
  interface Window {
    adsbygoogle: unknown[] & { 
      requestNonPersonalizedAds?: number;
      pauseAdRequests?: number;
    };
    __adsenseReady?: boolean;
    __consentModeSet?: boolean;
    __adsenseScriptLoaded?: boolean;
  }
}

const DEBUG_ADS = import.meta.env.DEV;

/**
 * Completely removes AdSense from the page
 * Call this when a user logs in as a paid user or when their tier changes to paid
 */
export const cleanupAdsense = async (): Promise<void> => {
  if (DEBUG_ADS) console.log('[AdSense Cleanup] Starting cleanup...');

  // 1. Pause ad requests first (prevents new ads from loading)
  if (window.adsbygoogle) {
    window.adsbygoogle.pauseAdRequests = 1;
    if (DEBUG_ADS) console.log('[AdSense Cleanup] Paused ad requests');
  }

  // 2. Remove the AdSense script from the DOM
  const script = document.querySelector('script[src*="pagead2.googlesyndication.com"]');
  if (script) {
    script.remove();
    if (DEBUG_ADS) console.log('[AdSense Cleanup] Removed AdSense script');
  }

  // 2b. Remove the dynamically injected AdSense account meta tag
  const adMeta = document.querySelector('meta[name="google-adsense-account"]');
  if (adMeta) {
    adMeta.remove();
    if (DEBUG_ADS) console.log('[AdSense Cleanup] Removed google-adsense-account meta tag');
  }

  // 3. Remove any other AdSense-related scripts
  const otherScripts = document.querySelectorAll('script[src*="googleads"], script[src*="adservice"]');
  otherScripts.forEach(s => s.remove());

  // 4. Clear all ad containers from the DOM
  const adContainers = document.querySelectorAll('.adsbygoogle');
  adContainers.forEach(container => {
    // Clear the content but leave the element (component will handle unmounting)
    container.innerHTML = '';
    // Remove data attributes to prevent reinitialization
    container.removeAttribute('data-ad-status');
    container.removeAttribute('data-adsbygoogle-status');
  });
  if (DEBUG_ADS && adContainers.length > 0) {
    console.log(`[AdSense Cleanup] Cleared ${adContainers.length} ad containers`);
  }

  // 5. Clear global AdSense state
  if (window.adsbygoogle) {
    // Clear the array but keep the reference
    window.adsbygoogle.length = 0;
  }
  
  // 6. Reset ready flags
  window.__adsenseReady = false;
  window.__adsenseScriptLoaded = false;

  // 7. Update consent mode to deny ads and clear cookies
  const { updateConsentMode: updateConsent, clearAdvertisingCookies } = await import('@/lib/consentManager');
  updateConsent(false, false);
  clearAdvertisingCookies();

  if (DEBUG_ADS) console.log('[AdSense Cleanup] Cleanup complete - all AdSense traces removed');
};

/**
 * Check if AdSense is currently loaded
 */
export const isAdsenseLoaded = (): boolean => {
  return !!document.querySelector('script[src*="pagead2.googlesyndication.com"]');
};

/**
 * Resume AdSense after cleanup/pause so free users (or post-logout) can fill again.
 * Must be called before (re)loading the script or pushing new units.
 */
export const resumeAdsenseRequests = (): void => {
  window.adsbygoogle = window.adsbygoogle || [];
  window.adsbygoogle.pauseAdRequests = 0;
  if (DEBUG_ADS) console.log('[AdSense Cleanup] Resumed ad requests (pauseAdRequests=0)');
};

/**
 * Reset the module-level script loaded flag in AdSenseLoader
 * This allows the script to be reloaded if the user logs out
 */
export const resetAdsenseLoaderState = (): void => {
  window.__adsenseScriptLoaded = false;
};
